import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";

// Bio sanitaria nella stanza. Paziente -> propria (/health-bio). Pro -> del paziente (/users/{peerId}/health-bio),
// SOLO con follow attivo. Sola lettura. Il backend resta l'autorita': su 401/403/404 i dati vengono CANCELLATI dallo stato.
// Rivalidazione: al mount, su focus finestra, su visibilitychange, alla riapertura (visible) e periodicamente (20s).
export function BioPanel({ peerId, isPro, visible = true }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true });
  const url = isPro ? `/users/${peerId}/health-bio` : "/health-bio";
  const inflight = useRef(false);

  const revalidate = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const { data } = await api.get(url);
      setState({ data: data.bio || data, loading: false });
    } catch (e) {
      const st = e.response?.status;
      // Prudenziale: su qualsiasi errore rimuoviamo i dati sanitari gia' mostrati (niente dati stantii nel DOM).
      if (st === 401 || st === 403 || st === 404) setState({ error: st, loading: false });
      else setState({ error: "net", loading: false });
    } finally {
      inflight.current = false;
    }
  }, [url]);

  useEffect(() => { revalidate(); }, [revalidate]);

  useEffect(() => {
    if (!visible) return;
    revalidate();
    const onFocus = () => revalidate();
    const onVis = () => { if (document.visibilityState === "visible") revalidate(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    const iv = setInterval(revalidate, 20000);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVis); clearInterval(iv); };
  }, [visible, revalidate]);

  const META = new Set(["user_id", "id", "_id", "field_status", "updated_at", "created_at", "nickname"]);
  const entries = state.data ? Object.entries(state.data).filter(([k, v]) => !META.has(k) && v !== null && v !== "" && typeof v !== "object") : [];

  return (
    <div className="flex flex-col h-full bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="video-bio-panel">
      <div className="px-3 py-2 border-b border-slate-100 font-semibold text-sm shrink-0">{t("video.bio_title")}</div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 text-sm" data-testid="video-bio-body">
        {state.loading ? <div className="text-slate-400">{t("common.loading")}</div>
          : state.error === "net" ? <div className="text-slate-500" data-testid="video-bio-unverifiable">{t("video.bio_unverifiable")}</div>
          : state.error ? <div className="text-slate-500" data-testid="video-bio-unauth">{state.error === 403 ? t("video.bio_unauth") : t("video.bio_error")}</div>
            : entries.length === 0 ? <div className="text-slate-400" data-testid="video-bio-empty">{t("video.bio_empty")}</div>
              : <dl className="space-y-2">{entries.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-slate-500">{t(`hbio.${k}`, { defaultValue: k.replace(/_/g, " ") })}</dt>
                  <dd className="whitespace-pre-wrap break-words">{String(v)}</dd>
                </div>))}</dl>}
      </div>
    </div>
  );
}
